import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import ContactForm from "../components/ContactForm";
import BigCTA from "../components/BigCTA";
import Button from "../components/Button";
import { VALUES } from "../data/content";

const About = () => {
  const aboutPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    mainEntity: { "@id": "https://www.weareonsports.com/#organization" },
    name: "Chi siamo — OnSports",
    description: "OnSports è la prima Impact Sports Consultancy italiana, fondata nel 2018 a Milano.",
  };

  return (
    <>
      <Seo
        title="Chi siamo: OnSports, Impact Sports Consultancy a Milano dal 2018"
        description="OnSports nasce a Milano nel 2018. Tre pillar di impatto per i brand sportivi: Business, Cultural, Social. 19 case study per AC Milan, Nike, Prada, Mizuno, Reale Mutua."
        path="/about"
        jsonLd={aboutPageJsonLd}
      />

      {/* HERO */}
      <section className="bg-dodgers text-galactico pt-40 pb-24 md:pt-52 md:pb-32" data-testid="about-hero">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Chi siamo</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <h1 className="display-h1 mt-8 max-w-[18ch]">
              La prima<br />
              <span className="text-lime">Impact Sports</span><br />
              <span className="text-lime">Consultancy</span><br />
              italiana.
            </h1>
          </RevealOnScroll>
          <RevealOnScroll delay={0.25}>
            <p className="mt-10 body-lg opacity-85 max-w-[64ch]">
              Non siamo consulenti generalisti che occasionalmente lavorano nello sport.
              Siamo un'azienda di consulenza verticale nel mondo sportivo che opera
              all'intersezione tra business strategy, contenuti strategici e tecnologia.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* CHI SIAMO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="about-intro">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>L'azienda</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Strategia, contenuti, tecnologia.<br />Sotto un'unica firma.</h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch]">
              <RevealOnScroll delay={0.15}>
                <p>
                  OnSports nasce dall'incontro tra consulenti strategici, content
                  producer, growth marketer e ingegneri di prodotto. Una squadra piccola
                  ma con un'idea chiara: lo sport è un asset enorme che la maggior parte
                  delle aziende sta utilizzando male.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Lavoriamo con brand, club, leghe, federazioni e atleti per costruire
                  programmi integrati che misurano un solo tipo di output: l'impatto.
                  Quello che si può documentare, replicare e portare al CdA.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  Siamo italiani, abbiamo sede a Milano e ragioniamo come una boutique
                  di consulenza: pochi clienti, relazioni lunghe, scorecard condivise
                  trimestralmente.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="about-mission">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La nostra mission</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Trasformare lo sport nella{" "}
                  <span className="text-lime">leva più potente</span> di impatto
                  misurabile.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Per troppo tempo gli investimenti nello sport si sono giustificati con
                  numeri di vanity: impression, reach, follower. La nostra missione è
                  spostare la conversazione sul terreno che conta: business case,
                  framework di misurazione, scorecard condivise, KPI tracciabili sui tre
                  pilastri Business / Cultura / Società.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Vogliamo costruire la categoria della "Impact Sports Consultancy" in
                  Italia e diffondere uno standard di lavoro che oggi non esiste:
                  evidence-based, integrato, ossessionato dai risultati di lungo periodo.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="about-vision">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La nostra vision</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Il punto di riferimento{" "}
                  <span className="text-lime">europeo</span> per la consulenza strategica
                  impact-driven nel mondo dello sport.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch]">
              <RevealOnScroll delay={0.15}>
                <p>
                  Da Milano vogliamo espanderci a Madrid, Parigi, Berlino, Londra
                  costruendo una rete di consulenti, partner editoriali e tecnologie
                  proprietarie che diventi lo standard del settore.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Non un'agenzia che apre uffici. Una metodologia che diventa
                  riferimento, supportata da tech (PitchPartner), ricerca proprietaria
                  (Sports Impact Observatory) e una community di sponsor evoluti.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* VALORI */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="about-valori">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>I nostri valori</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <h2 className="display-h2 mt-6 max-w-[18ch]">Cinque principi non negoziabili.</h2>
          </RevealOnScroll>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {VALUES.map((v, i) => (
              <RevealOnScroll key={v.num} delay={i * 0.06}>
                <div className="pillar-card p-7 md:p-9 border border-galactico/15 bg-galactico/[0.03]" data-testid={`value-${v.num}`}>
                  <span className="font-display text-mandrogni block leading-none mb-6" style={{ fontSize: "clamp(2.5rem, 4.5vw, 4rem)" }}>
                    {v.num}
                  </span>
                  <h3 className="font-display text-xl md:text-2xl leading-tight mb-4">{v.title}</h3>
                  <p className="text-[1rem] md:text-[1.0625rem] opacity-85 leading-relaxed">{v.body}</p>
                  <span className="mt-7 block h-[2px] w-12 bg-lime" />
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* CONTATTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="about-contatti">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>Iniziamo</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Iniziamo a costruire impatto insieme.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[44ch]">
                  Che tu sia un brand che vuole ripensare i propri investimenti nello sport,
                  una property che cerca nuovi modelli di monetizzazione, o un'organizzazione
                  con obiettivi ESG ambiziosi — parliamone.
                </p>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll delay={0.2}>
                <ContactForm tone="dark" />
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      <BigCTA />
    </>
  );
};

export default About;
